/**
 * JSON Text Element Schemas and Types
 *
 * Defines the schema and type definitions for json text elements
 * including transcriptions, start and end annotations and case annotations
 *
 * @module jsonTextElementSchemas
 */

import { z } from 'zod'

// ============= Schema Definitions =============
/**
 * Empty element
 */
const ParagraphSchema = z.object({
  tag: z.literal('p'),
  text: z.literal(''),
  element: z.literal('paragraph'),
  id: z.literal('paragraph_id')
})

/**
 * Base fields that all elements share
 */
const BaseSchema = z.object({
  id: z.string(), // Unique identifier for the element
  taskId: z.number(), // Task identifier
  tag: z.string(), // HTML tag (usually 'div')
  element: z.string(), // Type of element (used for discrimination)

  // Time fields
  timePosition: z.string(), // Formatted time "HH:mm:ss"
  timePositionSeconds: z.number(), // Total seconds from start
  timePositionHour: z.number(), // Hour component
  timePositionMinute: z.number(), // Minute component
  timePositionSecond: z.number() // Second component
})

/**
 * Schema for transcription element
 * Represents spoken content with timestamp and speaker information
 */
const TranscriptionSchema = BaseSchema.extend({
  element: z.literal('transcription'),
  text: z.string(), // Transcribed text content
  speaker: z.string(), // Speaker identifier
  timestampId: z.number().optional() // Unique identifier for the timestamp
})

/**
 * Schema for case element
 * Represents case boundaries with case metadata
 */
const CaseAnnotationSchema = BaseSchema.extend({
  element: z.enum(['StartBorder', 'RestartBorder', 'StopBorder']),
  creationTimeStamp: z.string(), // Timestamp of creation "DD/MM/YYYY HH:mm:ss"
  caseSerialNumber: z.union([z.string(), z.number()]), // Case serial number
  caseUid: z.union([z.string(), z.number()]), // Unique identifier for the case
  caseInsertType: z.union([z.string(), z.number()]), // Type of case insertion
  caseInsertTypeTitle: z.string(), // Title of case insertion type
  claimants: z.array(z.string()), // Array of claimant names
  defendants: z.array(z.string()), // Array of defendant names
  annotationTitle: z.string(), // Title of the annotation
  creatorId: z.string(), // ID of the creator

  // Optional fields
  snInTask: z.number().optional(), // Serial number in task
  eak: z.string().nullable().optional(), // Additional reference number
  gak: z.string().nullable().optional(), // Additional reference number
  subDocketCode: z.string().nullable().optional(), // Sub-docket reference
  taskNextStartTime: z.number().optional(), // Next start time in the task
  caseEndType: z.union([z.string(), z.number(), z.null()]).optional(), // Type of case ending
  caseEndTypeTitle: z.string().nullable().optional(), // Title of case ending type
  extraInfo: z.string().nullable().optional()
})

const CaseEndAnnotationSchema = CaseAnnotationSchema.extend({
  element: z.literal('EndBorder'),
  caseEndType: z.union([z.string(), z.number()]), // Type of case ending
  caseEndTypeTitle: z.string() // Title of case ending type
})

/**
 * Schema for annotation element
 * Represents various types of annotations (breaks, testimonies, etc.)
 */
const AnnotationStartSchema = BaseSchema.extend({
  element: z.enum([
    'BreakBorder',
    'TestemonyBorder',
    'ProsecutorSpeechBorder',
    'TreatmentBorder',
    'DefenseBorder',
    'AccusedBorder',
    'ReadableBorder',
    'CommentBorder',
    'CorrectionBorder'
  ]),
  creationTimeStamp: z.string(), // Timestamp of creation
  annotationTitle: z.string(), // Title of the annotation
  text: z.string().optional(), // Optional annotation text
  creatorId: z.string(), // ID of the creator
  snInTask: z.number().optional(), // Serial number in task
  taskNextStartTime: z.number().optional() // Next start time in the task
})

/**
 * Schema for annotation end elements in a court session
 * Represents the end of various annotation types
 */
const AnnotationEndSchema = BaseSchema.extend({
  element: z.enum([
    'BreakBorderStop',
    'TestemonyBorderStop',
    'ProsecutorSpeechBorderStop',
    'TreatmentBorderStop',
    'DefenseBorderStop',
    'AccusedBorderStop',
    'ReadableBorderStop'
  ]),
  creationTimeStamp: z.string(), // Timestamp of creation
  annotationTitle: z.string(), // Title of the annotation
  creatorId: z.string(), // ID of the creator
  snInTask: z.number().optional(), // Serial number in task
  taskNextStartTime: z.number().optional() // Next start time in the task
})

/**
 * Combined schema for all court session elements types
 */
const ElementSchema = z.discriminatedUnion('element', [
  ParagraphSchema,
  TranscriptionSchema,
  CaseAnnotationSchema,
  CaseEndAnnotationSchema,
  AnnotationStartSchema,
  AnnotationEndSchema
])

/**
 * Schema for an array of json text elements
 */
const ElementsArraySchema = z.array(ElementSchema)

/**
 * Type guard functions for different element types
 */
export const isTranscriptionElement = ({ element }) => element === 'transcription'

export const isCaseAnnotationElement = ({ element }) =>
  ['StartBorder', 'RestartBorder', 'StopBorder'].includes(element)

export const isCaseEndAnnotationElement = ({ element }) => element === 'EndBorder'

export const isAnnotationStartElement = ({ element }) =>
  [
    'BreakBorder',
    'TestemonyBorder',
    'ProsecutorSpeechBorder',
    'TreatmentBorder',
    'DefenseBorder',
    'AccusedBorder',
    'ReadableBorder',
    'CommentBorder',
    'CorrectionBorder'
  ].includes(element)

export const isAnnotationEndElement = ({ element }) =>
  [
    'BreakBorderStop',
    'TestemonyBorderStop',
    'ProsecutorSpeechBorderStop',
    'TreatmentBorderStop',
    'DefenseBorderStop',
    'AccusedBorderStop',
    'ReadableBorderStop'
  ].includes(element)

// ============= Validation Functions =============

/**
 * Validates a single court session element
 */
export const validateJsonTextElement = (data) => {
  return ElementSchema.parse(data)
}

/**
 * Validates an array of court session elements
 */
export const validateJsonTextElements = (data) => {
  return ElementsArraySchema.parse(data)
}

// ============= Schema Exports =============

export {
  ElementSchema,
  ElementsArraySchema,
  TranscriptionSchema,
  CaseAnnotationSchema,
  CaseEndAnnotationSchema,
  AnnotationStartSchema,
  AnnotationEndSchema
}
